import * as React from "react";
import { ViewState, EditingState } from "@devexpress/dx-react-scheduler";
import {
  Scheduler,
  Resources,
  Appointments,
  AppointmentTooltip,
  DayView,
  WeekView,
  MonthView,
  EditRecurrenceMenu,
  AllDayPanel,
  ConfirmationDialog,
  Toolbar,
  DateNavigator,
  TodayButton,
  ViewSwitcher,
} from "@devexpress/dx-react-scheduler-material-ui";
import {
  appointments as defaultAppointments,
  projects,
  priorities,
} from "./appointments";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

// --- NUEVO COMPONENTE PARA EL ENCABEZADO DE DÍA EN LA VISTA DE SEMANA ---
const ClickableDayScaleCell = ({ onClick, ...restProps }) => (
  <WeekView.DayScaleCell
    {...restProps}
    onClick={() => onClick(restProps.startDate)}
    style={{ cursor: "pointer" }} // Añade un cursor para indicar que es clickeable
  />
);

// Componente para la celda del Mes, ahora más simple
const ClickableMonthTimeTableCell = ({ onClick, ...restProps }) => (
  <MonthView.TimeTableCell
    {...restProps}
    onClick={() => onClick(restProps.startDate)}
    style={{ cursor: "pointer" }}
  />
);

// Componente estándar para la vista de Día (no necesita ser clickeable)
const TimeTableCellDay = (props) => <DayView.TimeTableCell {...props} />;

// Funciones de localización (sin cambios)
const allDayLocalizationMessages = { "es-ES": { allDay: "Todo el día" } };
const getAllDayMessages = (locale) => allDayLocalizationMessages[locale];
const toDayLocalizationMessages = { "es-ES": { today: "Hoy" } };
const getToDayMessages = (locale) => toDayLocalizationMessages[locale];
const confirmationDialogLocalizationMessages = {
  "es-ES": {
    discardButton: "Descartar",
    deleteButton: "Borrar",
    cancelButton: "Cancelar",
    confirmDeleteMessage: "¿Está seguro de eliminar este evento?",
    confirmCancelMessage: "¿Descartar cambios?",
  },
};
const getConfirmationDialogMessages = (locale) =>
  confirmationDialogLocalizationMessages[locale];
const editRecurrenceLocalizationMessages = {
  "es-ES": {
    current: "Este evento",
    currentAndFollowing: "Este y los siguientes eventos",
    all: "Todos los eventos",
    menuEditingTitle: "Editar evento recurrente",
    menuDeletingTitle: "Eliminar evento recurrente",
    cancelButton: "Cancelar",
    commitButton: "Aceptar",
  },
};
const getEditRecurrenceMessages = (locale) =>
  editRecurrenceLocalizationMessages[locale];

export default class DashBoardCalendar extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      data: props.data,
      locale: "es-ES",
      addedAppointment: {},
      appointmentChanges: {},
      editingAppointment: undefined,
      resources: [
        {
          fieldName: "priorities",
          title: "Prioridades",
          instances: priorities,
        },
        { fieldName: "projects", title: "Proyectos", instances: projects },
      ],
      currentDate: dayjs().toDate(),
      currentViewName: "Semana", // La vista por defecto es 'Semana'
    };
    this.commitChanges = this.commitChanges.bind(this);
    this.changeAddedAppointment = this.changeAddedAppointment.bind(this);
    this.changeAppointmentChanges = this.changeAppointmentChanges.bind(this);
    this.changeEditingAppointment = this.changeEditingAppointment.bind(this);
    this.changeCurrentViewName = this.changeCurrentViewName.bind(this);
    this.changeCurrentDate = this.changeCurrentDate.bind(this);
    this.handleHeaderClick = this.handleHeaderClick.bind(this);
  }

  // Handlers para controlar el estado
  changeCurrentViewName(viewName) {
    this.setState({ currentViewName: viewName });
  }

  changeCurrentDate(newDate) {
    this.setState({ currentDate: newDate });
  }

  // Lógica principal: al hacer clic en un encabezado o celda, cambia a la vista de día
  handleHeaderClick(date) {
    this.setState({
      currentDate: date,
      currentViewName: "Día",
    });
  }

  // Lógica de edición (sin cambios)
  changeAddedAppointment(addedAppointment) {
    this.setState({ addedAppointment });
  }
  changeAppointmentChanges(appointmentChanges) {
    this.setState({ appointmentChanges });
  }
  changeEditingAppointment(editingAppointment) {
    this.setState({ editingAppointment });
  }
  commitChanges({ added, changed, deleted }) {
    this.setState((state) => {
      let { data } = state;
      if (added) {
        const startingAddedId =
          data.length > 0 ? data[data.length - 1].id + 1 : 0;
        data = [...data, { id: startingAddedId, ...added }];
      }
      if (changed) {
        data = data.map((appointment) =>
          changed[appointment.id]
            ? { ...appointment, ...changed[appointment.id] }
            : appointment
        );
      }
      if (deleted !== undefined) {
        data = data.filter((appointment) => appointment.id !== deleted);
      }
      return { data };
    });
  }

  render() {
    const {
      data,
      resources,
      addedAppointment,
      appointmentChanges,
      editingAppointment,
      locale,
      currentDate,
      currentViewName,
    } = this.state;
    const { height } = this.props;

    return (
      <Scheduler data={data} height={height || 450} locale={locale}>
        <ViewState
          currentDate={currentDate}
          currentViewName={currentViewName}
          onCurrentDateChange={this.changeCurrentDate}
          onCurrentViewNameChange={this.changeCurrentViewName}
        />
        <EditingState
          onCommitChanges={this.commitChanges}
          addedAppointment={addedAppointment}
          onAddedAppointmentChange={this.changeAddedAppointment}
          appointmentChanges={appointmentChanges}
          onAppointmentChangesChange={this.changeAppointmentChanges}
          editingAppointment={editingAppointment}
          onEditingAppointmentChange={this.changeEditingAppointment}
        />
        <WeekView
          name="Semana"
          startDayHour={8}
          endDayHour={17}
          // --- ESTA ES LA LÍNEA MODIFICADA ---
          // Hacemos que el encabezado del día sea clickeable
          dayScaleCellComponent={(props) => (
            <ClickableDayScaleCell
              {...props}
              onClick={this.handleHeaderClick}
            />
          )}
        />
        <DayView
          name="Día"
          startDayHour={8}
          endDayHour={17}
          timeTableCellComponent={TimeTableCellDay}
        />
        <MonthView
          name="Mes"
          // --- ESTAS SON LAS LÍNEAS MODIFICADAS ---
          // Ocultamos los eventos para evitar el abarrotamiento
          maxAppointmentsPerCell={0}
          // Hacemos que toda la celda sea clickeable
          timeTableCellComponent={(props) => (
            <ClickableMonthTimeTableCell
              {...props}
              onClick={this.handleHeaderClick}
            />
          )}
        />
        <Toolbar />
        <ViewSwitcher />
        <DateNavigator />
        <TodayButton messages={getToDayMessages(locale)} />
        <AllDayPanel messages={getAllDayMessages(locale)} />
        <EditRecurrenceMenu messages={getEditRecurrenceMessages(locale)} />
        <ConfirmationDialog messages={getConfirmationDialogMessages(locale)} />
        <Appointments />
        <Resources data={resources} mainResourceName="priorities" />
        <AppointmentTooltip showCloseButton />
      </Scheduler>
    );
  }
}
